import { useTRPC, type AppRouter } from "#trpc";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
    hasAppPermission,
    type AppPermissionIdentifierWithoutWildcard,
    type AppPermissionIdentifierWithEffect,
} from "@dmca/models/interface/auth";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { notFound, useRouteContext } from "@tanstack/react-router";
import superjson from "@dmca/utils/superjson";
import type { ApplicationUser } from "./users";

/**
 * Checks if the user has a specific permission in the loader (or anywhere with access to the router context).
 * @param permission Permission to check
 * @param opts Router context
 * @example
 * export const Route = createFileRoute("/")({
    component: RouteComponent,
    loader: async ({ context }) => {
        await checkPermissionsInLoader("Admin::UpdateCell", context);
    },
 * });
 */
export const checkPermissionsInLoader = async (
    permission: AppPermissionIdentifierWithoutWildcard,
    opts: {
        trpcQcProxy: TRPCOptionsProxy<AppRouter>;
        qc: QueryClient;
        user: ApplicationUser;
    }
) => {
    const { trpcQcProxy, qc, user } = opts;
    const tenantPermissions = await qc.ensureQueryData(
        trpcQcProxy.auth.getTenantPermissions.queryOptions()
    );

    const hasPermission = userHasPermission(
        permission,
        user,
        tenantPermissions
    );
    if (!hasPermission) {
        throw notFound({
            routeId: "/" as any, // needed to avoid tsc errors
        });
    }
};

export const returnPermissionsInLoader = async (
    permission: AppPermissionIdentifierWithoutWildcard,
    opts: {
        trpcQcProxy: TRPCOptionsProxy<AppRouter>;
        qc: QueryClient;
        user: ApplicationUser;
    }
) => {
    const { trpcQcProxy, qc, user } = opts;
    const tenantPermissions = await qc.ensureQueryData(
        trpcQcProxy.auth.getTenantPermissions.queryOptions()
    );

    const hasPermission = userHasPermission(
        permission,
        user,
        tenantPermissions
    );

    return hasPermission;
};

/**
 * Checks if the user has a specific permission in the following order:
 *
 * 1. Function first checks for dev permission override, which is only valid in dev envs.
 * 2. It then checks tenant permissions. Tenant deny permissions overrides user allow permissions.
 * 3. It then checks specific user permissions. Explicit user permissions override role permissions.
 * 4. Finally, it checks user role permissions.
 *
 * The order can be overridden by passing the `type` parameter.
 *
 * @param permission Permission to check
 * @param user Application user
 * @param tenantPermissions Tenant permissions
 * @param type Optional type to specify which permission set to check against:
 * - "tenant" for tenant permissions
 * - "role" for user role permissions
 * - "user" for user permissions
 * @returns true if the user has the permission, false if not
 */
export function userHasPermission(
    permission: AppPermissionIdentifierWithoutWildcard,
    user: ApplicationUser | undefined,
    tenantPermissions: AppPermissionIdentifierWithEffect[] = [],
    type?: "tenant" | "role" | "user"
) {
    if (!user) return false;

    let permissionOverride: AppPermissionIdentifierWithEffect[] = [];
    if (import.meta.env.DEV && typeof window !== "undefined") {
        const permissionOverrideString = sessionStorage.getItem(
            "dev__permission_override"
        );
        if (permissionOverrideString) {
            try {
                permissionOverride = superjson.parse(permissionOverrideString);
            } catch {
                /** empty */
            }
        }
    }

    if (permissionOverride.length > 0) {
        const permissionOverrideHasPermission = hasAppPermission(
            permission,
            permissionOverride,
            "loose"
        );
        if (permissionOverrideHasPermission !== undefined) {
            return permissionOverrideHasPermission;
        }
    }

    const tenantHasPermission = hasAppPermission(permission, tenantPermissions);
    const userRoleHasPermission = hasAppPermission(
        permission,
        user.rolePermissions
    );
    const userHasPermission = hasAppPermission(
        permission,
        user.userPermissions,
        "loose"
    );

    if (type) {
        if (type === "tenant") return tenantHasPermission ?? false;
        if (type === "role") return userRoleHasPermission ?? false;
        if (type === "user") return userHasPermission ?? false;
    }

    if (!tenantHasPermission) return false;
    if (userHasPermission !== undefined) return userHasPermission;
    return userRoleHasPermission;
}

/**
 * Wraps the `userHasPermission` function in a React hook.
 * @returns A function that checks if the user has a specific permission
 * @example
 * const hasPermission = useAppPermissions();
 * const isAuthorized = hasPermission("Admin::UpdateCell");
 */
export function useAppPermissions(type?: "tenant" | "role" | "user") {
    const trpc = useTRPC();
    const { user } = useRouteContext({ strict: false }) as {
        user: ApplicationUser | undefined;
    };
    const { data: tenantPermissions = [] } = useQuery(
        trpc.auth.getTenantPermissions.queryOptions()
    );

    const hasPermissionInner = useCallback(
        (permission: AppPermissionIdentifierWithoutWildcard) => {
            return userHasPermission(permission, user, tenantPermissions, type);
        },
        [tenantPermissions, user, type]
    );

    return hasPermissionInner;
}
